<template>
  <div class="flex flex-col w-full">
    <div v-if="isLoading" class="flex flex-col gap-2 py-2">
      <Skeleton v-for="n of 6" :key="n" height="3.6rem" borderRadius="0.75rem" />
    </div>

    <retry-loader v-else-if="isErrorState" :isErrorState="isErrorState" @retry-function="reload" />

    <div v-else-if="!rows.length" class="flex flex-col items-center justify-center py-14 text-gray-400 gap-2">
      <i class="pi pi-inbox text-3xl" />
      <span class="text-sm">עדיין לא נשלחו בקשות תשלום</span>
    </div>

    <div v-else class="card !py-0.5">
      <div
        v-for="(row, idx) of rows"
        :key="row._id"
        v-wave
        class="flex items-start gap-2 py-3"
        :class="[idx !== rows.length - 1 && 'border-b border-gray-100', isOpenable(row) ? 'cursor-pointer' : '']"
        @click="openRow(row)"
      >
        <div class="flex flex-row w-full items-center">
          <div class="flex flex-col gap-1 min-w-0">
            <span class="font-medium text-[13.5px] truncate">{{ title(row) }}</span>
            <span class="text-xs text-gray-400">{{ whenLabel(row) }}</span>
          </div>
          <div class="mr-auto flex flex-col items-end gap-1 shrink-0 ps-2">
            <span class="text-[13.5px] font-bold">{{ formatCurrency(row.paidPrice ?? row.price) }}</span>
            <span :class="['text-xs flex gap-1 items-center px-2 rounded', statusChip(row).class]">
              <span>{{ statusChip(row).label }}</span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- infinite scroll sentinel -->
    <div ref="sentinel" class="h-8 flex items-center justify-center">
      <i v-if="isLoadingMore" class="pi pi-spin pi-spinner text-gray-400" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { DateTime } from 'luxon';
import Skeleton from 'primevue/skeleton';
import {
  IPaymentsResponse,
  IPaymentTransactionRow,
  PaymentRequestReason,
  PaymentRequestStatus,
} from '@torim-app/types';
import { useDrawer } from '@torim-app/shared-components';

import PaymentDetailsDrawer from '@app/components/admin/pages/customer/PaymentDetailsDrawer.vue';
import { PaymentsApi } from '@app/api/admin';
import { formatCurrency } from '@app/components/admin/analytics/analytics-charts';
import { trackEvent } from '@app/composables/posthog';

const PAGE_SIZE = 25;

const { openDrawer } = useDrawer();

const rows = ref<IPaymentTransactionRow[]>([]);
const isLoading = ref(true);
const isLoadingMore = ref(false);
const isErrorState = ref(false);
const hasMore = ref(false);
const sentinel = ref<HTMLElement>();

let observer: IntersectionObserver | undefined;

const fetchPage = async (reset = false) => {
  try {
    isErrorState.value = false;
    if (reset) {
      isLoading.value = true;
      rows.value = [];
    } else {
      isLoadingMore.value = true;
    }
    const data = await PaymentsApi.getRecentPayments(reset ? 0 : rows.value.length, PAGE_SIZE);
    rows.value = reset ? data.transactions : [...rows.value, ...data.transactions];
    hasMore.value = data.hasMore;
  } catch (e) {
    console.error(e);
    // Only blank the tab when the very first page failed — a failed "load more"
    // must not throw away what is already on screen.
    if (reset) isErrorState.value = true;
  } finally {
    isLoading.value = false;
    isLoadingMore.value = false;
  }
};

const reload = () => fetchPage(true);

const loadMore = () => {
  if (isLoading.value || isLoadingMore.value || !hasMore.value) return;
  fetchPage();
};

// ─── row presentation (mirrors the customer profile's payments list) ──────────
const title = (row: IPaymentTransactionRow): string => {
  const who = row.customerName ? ` · ${row.customerName}` : '';
  return `${reasonLabel(row)}${who}`;
};

const reasonLabel = (row: IPaymentTransactionRow): string => {
  switch (row.paymentReason) {
    case PaymentRequestReason.Visit:
    case PaymentRequestReason.Schedule:
      return row.description || 'ביקור';
    case PaymentRequestReason.Advanced:
      return 'מקדמה';
    case PaymentRequestReason.Voucher:
      return 'תשלום עבור שובר';
    case PaymentRequestReason.BundlePack:
      return 'רכישת כרטיסייה';
    case PaymentRequestReason.ProductOrder:
      return 'הזמנת מוצרים';
    default:
      return row.description ? `תשלום כללי - ${row.description}` : 'תשלום כללי';
  }
};

const whenLabel = (row: IPaymentTransactionRow): string => {
  const iso = row.chargedDate ?? row.createdAt ?? row.date;
  if (!iso) return '';
  const dt = DateTime.fromISO(iso).setZone('Asia/Jerusalem');
  if (!dt.isValid) return '';
  const date = dt.toLocaleString(DateTime.DATE_MED, { locale: 'he-IL' });
  return row.chargedDate ? `חויב בתאריך ${date}` : `נשלח בתאריך ${date}`;
};

const statusChip = (row: IPaymentTransactionRow): { label: string; class: string } => {
  if (row.status === PaymentRequestStatus.Refunded) return { label: 'הוחזר', class: 'text-orange-500 bg-orange-50' };
  if (row.status === PaymentRequestStatus.PartiallyRefunded) return { label: 'הוחזר חלקית', class: 'text-orange-500 bg-orange-50' };
  if (row.status === PaymentRequestStatus.Success) return { label: 'שולם', class: 'text-green-600 bg-green-50' };
  if (row.status === PaymentRequestStatus.Scheduled) return { label: 'כרטיס נשמר', class: 'text-blue-600 bg-blue-50' };
  if (row.status === PaymentRequestStatus.InProgress) return { label: 'בתהליך', class: 'text-gray-500 bg-gray-50' };
  if (row.status === PaymentRequestStatus.Failed) return { label: 'נכשל', class: 'text-red-500 bg-red-50' };
  if (row.status === PaymentRequestStatus.Cancelled) return { label: 'בוטל', class: 'text-gray-500 bg-gray-50' };
  return { label: 'נשלחה בקשה', class: 'text-gray-500 bg-gray-50' };
};

// Only a paid/refunded request has anything to show (and to refund) in the details drawer
const OPENABLE_STATUSES = [
  PaymentRequestStatus.Success,
  PaymentRequestStatus.Refunded,
  PaymentRequestStatus.PartiallyRefunded,
];
const isOpenable = (row: IPaymentTransactionRow) => OPENABLE_STATUSES.includes(row.status);

const openRow = (row: IPaymentTransactionRow) => {
  if (!isOpenable(row)) return;
  trackEvent('payments_recent_row_opened', { status: row.status });
  openDrawer(PaymentDetailsDrawer, {
    selectedPayment: { ...row } as unknown as IPaymentsResponse,
    onRefunded: (updated: IPaymentsResponse) => {
      const idx = rows.value.findIndex(r => r._id === updated._id);
      if (idx !== -1) {
        rows.value[idx] = {
          ...rows.value[idx],
          status: updated.status,
          refundedAmount: updated.refundedAmount,
          refunds: updated.refunds,
        };
      }
    },
  }, { title: 'פרטי תשלום' });
};

onMounted(() => {
  fetchPage(true);
  // root: null — intersection is computed against the viewport with ancestor clipping
  // applied, so this works whether the page or the tab's own overflow does the scrolling.
  observer = new IntersectionObserver(entries => {
    if (entries.some(e => e.isIntersecting)) loadMore();
  }, { rootMargin: '200px' });
  if (sentinel.value) observer.observe(sentinel.value);
});

onBeforeUnmount(() => observer?.disconnect());
</script>

<style scoped lang="scss">
.card {
  background: #fff;
  border: 1px solid rgb(228 228 231);
  border-radius: 18px;
  padding: 14px 15px;
}
</style>
